import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Upload, Cpu, Download } from 'lucide-react';

export const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const steps = [
    {
      icon: Upload,
      number: '01',
      title: 'Upload your document',
      description: 'Send your invoice, receipt, or PDF through our API or dashboard. We accept most common formats.',
    },
    {
      icon: Cpu,
      number: '02',
      title: 'Our AI processes the file',
      description: 'Advanced OCR and machine learning models extract and structure the data automatically.',
    },
    {
      icon: Download,
      number: '03',
      title: 'Receive structured data',
      description: 'Get clean JSON or CSV data via webhook or API response, ready for your application.',
    },
  ];

  return (
    <section
      ref={ref}
      id="how-it-works"
      className="py-20 md:py-28 bg-white"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#635BFF]/10 text-[#635BFF] text-sm font-medium rounded-full mb-4">
            How It Works
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Three Simple Steps
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Get started in minutes with our straightforward integration process.
          </p>
        </motion.div>

        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#635BFF]/20 to-transparent transform -translate-y-1/2" />

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-3xl p-8 border border-gray-100 hover:border-[#635BFF]/30 hover:shadow-xl transition-all duration-300 relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#635BFF] to-[#7C3AED] rounded-2xl flex items-center justify-center shadow-lg shadow-[#635BFF]/20">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="font-heading text-5xl font-bold text-[#F7F8FC]">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#0A2540] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#425466] leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-20">
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center"
                    >
                      <svg
                        className="w-5 h-5 text-[#635BFF]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
